import React from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Bell, AlertTriangle, Award, ClipboardCheck, TestTube, X, Check, ChevronRight } from 'lucide-react';
import api from '@/lib/api';
import { formatDistanceToNow } from 'date-fns';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  entityId?: string | null;
}

const typeIconMap: Record<string, { icon: typeof Bell; color: string }> = {
  cert_expiry_critical: { icon: Award,          color: '#DC2626' },
  cert_expiry_warning:  { icon: Award,          color: '#D97706' },
  cert_expiry:          { icon: Award,          color: '#D97706' },
  capa_deadline_critical: { icon: AlertTriangle, color: '#DC2626' },
  capa_overdue:         { icon: AlertTriangle,  color: '#D97706' },
  capa_deadline:        { icon: AlertTriangle,  color: '#D97706' },
  test_request_submitted: { icon: TestTube,     color: '#2563EB' },
  audit_finding_critical: { icon: ClipboardCheck, color: '#DC2626' },
  audit_due:            { icon: ClipboardCheck, color: '#b07d1a' },
  workflow_update:      { icon: ClipboardCheck, color: '#b07d1a' },
  batch_released:       { icon: Bell,           color: '#16A34A' },
  system:               { icon: Bell,           color: '#6899A0' },
};

// Map notification types to app routes
const typeRouteMap: Record<string, string> = {
  cert_expiry_critical:    '/certifications',
  cert_expiry_warning:     '/certifications',
  cert_expiry:             '/certifications',
  capa_deadline_critical:  '/capa',
  capa_overdue:            '/capa',
  capa_deadline:           '/capa',
  test_request_submitted:  '/requests',
  audit_finding_critical:  '/audits',
  audit_due:               '/audits',
  workflow_update:         '/workflows',
  batch_released:          '/bmr',
  system:                  '/dashboard',
};

export default function NotificationPanel({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: async () => {
      const { data } = await api.get('/notifications');
      return data.data as Notification[];
    },
  });

  const markRead = useMutation({
    mutationFn: (id: string) => api.put(`/notifications/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => api.put('/notifications/mark-all-read'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const handleNotifClick = (notif: Notification) => {
    if (!notif.isRead) markRead.mutate(notif.id);
    const base = typeRouteMap[notif.type] || '/dashboard';
    const route = notif.entityId ? `${base}?openId=${notif.entityId}` : base;
    onClose();
    navigate(route);
  };

  const handleViewAll = () => {
    onClose();
    navigate('/notifications');
  };

  const hasUnread = (data || []).some((n) => !n.isRead);
  const unreadCount = (data || []).filter((n) => !n.isRead).length;

  return (
    <motion.div
      data-testid="notification-panel"
      className="fixed top-14 right-4 w-96 bg-card rounded-xl border border-border shadow-2xl z-40 overflow-hidden"
      initial={{ opacity: 0, y: -8, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.97 }}
      transition={{ duration: 0.15 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <h3 className="font-serif font-semibold text-sm text-text">Notifications</h3>
          {unreadCount > 0 && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white leading-none">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasUnread && (
            <button
              onClick={() => markAllReadMutation.mutate()}
              disabled={markAllReadMutation.isPending}
              className="text-[11px] text-teal hover:text-teal-dark font-medium transition-colors disabled:opacity-50"
              title="Mark all as read"
            >
              Mark all read
            </button>
          )}
          <button onClick={onClose} className="text-text-dim hover:text-text transition-colors">
            <X size={14} />
          </button>
        </div>
      </div>

      {/* List */}
      <div className="max-h-[400px] overflow-y-auto divide-y divide-border/50">
        {isLoading && (
          <div className="p-4 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex gap-3 animate-pulse">
                <div className="w-8 h-8 bg-card2 rounded-full flex-shrink-0" />
                <div className="flex-1 space-y-1">
                  <div className="h-2.5 bg-card2 rounded w-3/4" />
                  <div className="h-2 bg-card2 rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!isLoading && data?.length === 0 && (
          <div className="py-10 text-center">
            <Bell size={24} className="text-text-dim mx-auto mb-2" />
            <p className="text-sm text-text-muted">All caught up!</p>
          </div>
        )}

        {data?.map((notif) => {
          const cfg = typeIconMap[notif.type] || typeIconMap.system;
          const Icon = cfg.icon;
          return (
            <div
              key={notif.id}
              onClick={() => handleNotifClick(notif)}
              className={`group flex gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-bg ${
                notif.isRead ? 'opacity-60' : 'bg-teal/[0.03]'
              }`}
            >
              {/* Unread dot */}
              <div className="flex-shrink-0 mt-1.5">
                {!notif.isRead
                  ? <div className="w-2 h-2 rounded-full bg-teal-500" />
                  : <div className="w-2 h-2" />
                }
              </div>

              {/* Icon */}
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{ background: `${cfg.color}18` }}
              >
                <Icon size={13} style={{ color: cfg.color }} />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-text leading-snug">{notif.title}</p>
                <p className="text-xs text-text-muted mt-0.5 line-clamp-2 leading-snug">{notif.message}</p>
                <p className="text-[10px] text-text-dim mt-1">
                  {formatDistanceToNow(new Date(notif.createdAt), { addSuffix: true })}
                </p>
              </div>

              {/* Arrow (shows on hover) */}
              <ChevronRight
                size={13}
                className="flex-shrink-0 mt-1.5 text-text-dim opacity-0 group-hover:opacity-100 transition-opacity"
              />
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="px-4 py-2.5 border-t border-border text-center">
        <button
          onClick={handleViewAll}
          className="text-xs text-teal hover:text-teal-dark transition-colors font-medium flex items-center gap-1 mx-auto"
        >
          View all notifications
          <ChevronRight size={11} />
        </button>
      </div>
    </motion.div>
  );
}
